'use client'

import { useContext } from 'react'
import { ConfigContext, useTranslation } from '../../../context/ConfigContext'
import { getIsInStock, isSellingProductOnline } from '../../../utils/Product.utils'
import { useIsMobileOrTablet } from '../../../utils/screen.utils'
import { Button } from '../../Button/Button'
import ProductGetNotifyInStock from '../ProductGetNotifyInStock'
import {
  ProductConfigurationDeliveryWording,
  ProductConfigurationMaybeOnStore,
} from './ProductConfigurationCta.utils'
import { gtmCategory, gtmPush } from '../../../utils/gtmHelpers'
import OrderByPhoneCta from './OrderByPhoneCta'
import Loader from '../../Loader/Loader'
import ContactConciergerieCta from './ContactConciergerieCta'

const getIsOnStockBundleProduct = (myMoveVariant: Variant, braceletMyMoveVariant: Variant) => {
  const isMyMoveVariantInStock = !!(myMoveVariant.product && getIsInStock(myMoveVariant.product))
  const isBraceletMyMoveVariantInStock = !!(
    braceletMyMoveVariant.product && getIsInStock(braceletMyMoveVariant.product)
  )

  const isOnStock = isMyMoveVariantInStock && isBraceletMyMoveVariantInStock

  if (isOnStock) {
    return { isOnStock: true }
  }

  const productOutOfStock = !isMyMoveVariantInStock ? myMoveVariant : braceletMyMoveVariant

  return { isOnStock: false, productOutOfStock }
}

const getIsSellingProductOnlineBundleProduct = (
  myMoveVariant: Variant,
  braceletMyMoveVariant: Variant,
  isEcommerce: boolean,
) => {
  const isMyMoveVariantSelling = !!(
    myMoveVariant.product && isSellingProductOnline(myMoveVariant.product, isEcommerce)
  )
  const isBraceletMyMoveVariantSelling = !!(
    braceletMyMoveVariant.product &&
    isSellingProductOnline(braceletMyMoveVariant.product, isEcommerce)
  )

  return isMyMoveVariantSelling && isBraceletMyMoveVariantSelling
}

type BundleProductStatus =
  | {
      status: 'INVALID_FORM' | 'NOT_SELLING' | 'IN_STOCK'
    }
  | {
      status: 'OUT_OF_STOCK'
      productOutOfStock?: Variant
    }

const getBundleProductStatus = (
  selectedVariants: Array<Variant | null> | null,
  isEcommerce: boolean,
): BundleProductStatus => {
  if (!selectedVariants) {
    return { status: 'INVALID_FORM' }
  }

  const [myMoveVariant, braceletMyMoveVariant] = selectedVariants

  if (!myMoveVariant || !braceletMyMoveVariant) {
    return { status: 'INVALID_FORM' }
  }

  const isSelling = getIsSellingProductOnlineBundleProduct(
    myMoveVariant,
    braceletMyMoveVariant,
    isEcommerce,
  )

  if (!isSelling) {
    return { status: 'NOT_SELLING' }
  }

  const isOnStockStatus = getIsOnStockBundleProduct(myMoveVariant, braceletMyMoveVariant)

  if (!isOnStockStatus.isOnStock) {
    return { status: 'OUT_OF_STOCK', productOutOfStock: isOnStockStatus.productOutOfStock }
  }

  return { status: 'IN_STOCK' }
}

const ProductConfigurationCtaBundleProduct = ({
  isLoading = false,
  selectedVariants,
  onSubmit,
  product,
}: {
  isLoading: boolean
  product: Product
  selectedVariants: Array<Variant | null> | null
  onSubmit: () => void
}) => {
  const isTablet = useIsMobileOrTablet()
  const { isEcommerce } = useContext(ConfigContext)
  const t = useTranslation()
  const isSelling = isSellingProductOnline(product, isEcommerce());

  const bundleProductStatus = getBundleProductStatus(selectedVariants, isEcommerce())

  const { storeConfig } = useContext(ConfigContext)
  const tel = storeConfig.general_store_information_phone?.replace(/\s/g, '')

  const handleClick = () => {
    const storedValues = JSON.parse(window.localStorage.getItem('productValues') as any)
    const productItems: any[] = []

    selectedVariants?.map((variant) => {
      let obj = {}
      const gtmCategoryObj = gtmCategory(variant?.product?.gtm_categories)

      obj['item_name'] = variant?.product?.gtm_name
      obj['item_id'] = variant?.product?.sku
      obj['item_brand'] = 'Messika'
      obj['item_price'] = variant?.product?.price_range?.minimum_price?.regular_price.value
      obj['item_index'] = storedValues?.position ?? ''
      obj['currency'] = variant?.product?.price_range?.minimum_price?.regular_price.currency
      obj['value'] = variant?.product?.prices?.row_total?.value
      obj['item_list_id'] = storedValues ? storedValues.listId : 'produit'
      obj['item_list_name'] = storedValues ? storedValues.listName : 'Produit'
      obj = { ...obj, ...gtmCategoryObj }

      productItems.push(obj)
    })

    gtmPush({
      event: 'order_by_phone',
      ecommerce: {
        items: productItems,
      },
    })
  }

  if (!isSelling) {
    if (product.isFineJewelry) {
      return (
        <ContactConciergerieCta />
      );
    }
    return <OrderByPhoneCta product={product} />
  }

  if (bundleProductStatus.status === 'INVALID_FORM' && isEcommerce()) {
    return (
      <>
        {isTablet && (
          <p className='complet-options'>{t('catalog-product-mymove-requirement', {}, true)}</p>
        )}
        <Button
          isLoading={isLoading}
          classname='black'
          label={t('Add to Cart', {}, false)}
          style='dark'
          type='submit'
          disabled
        />
        <ProductConfigurationDeliveryWording />
      </>
    )
  }

  if (bundleProductStatus.status === 'NOT_SELLING' && tel) {
    if (isTablet) {
      return (
        <>
          <ProductConfigurationMaybeOnStore />
          <a href={`tel:${tel}`} className='button black' onClick={handleClick}>
            {t('Contact us', {}, false)}
          </a>
        </>
      )
    }

    return (
      <>
        <a href={`tel:${tel}`} className='button black' onClick={handleClick}>
          {t('Contact us', {}, false)}
        </a>
        <ProductConfigurationMaybeOnStore />
      </>
    )
  }

  // alert stock
  if (bundleProductStatus.status === 'OUT_OF_STOCK') {
    const { productOutOfStock } = bundleProductStatus

    if (!productOutOfStock?.product) {
      return null
    }

    if (isTablet) {
      return (
        <>
          <ProductConfigurationMaybeOnStore />
          <ProductGetNotifyInStock product={productOutOfStock.product} />
        </>
      )
    }

    return (
      <>
        <ProductGetNotifyInStock product={productOutOfStock.product} />
        <ProductConfigurationMaybeOnStore />
      </>
    )
  }

  if (product.stock_status === 'Loading') {
    return (
      <div>
        <Loader small />
      </div>
    )
  }

  // IN_STOCK
  return (
    <>
      {isEcommerce() && (
        <>
          <Button
            classname='black'
            label={t('Add to Cart', {}, false)}
            style='dark'
            type='submit'
            onClick={onSubmit}
          />
          <ProductConfigurationDeliveryWording />
        </>
      )}
    </>
  )
}

export default ProductConfigurationCtaBundleProduct
